import React, {
  useState,
  useContext,
  createContext,
  ReactElement,
  Dispatch,
  SetStateAction,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import useBreakpointQuery from '../hooks/useBreakPointQuery';
import { useApp } from './AppContext';

interface IUseProfile {
  gallerySwiper: any;
  setGallerySwiper: Dispatch<SetStateAction<any>>;
  offerBoxContainerRef: any;
  photoAspectRatioRef: any;
  profileCardBannerRef: any;
  profileDetailsRef: any;
  calculateGalleryMediaRatio: () => void;
  galleryMediaRatioValue: string;
  animateDirection: string;
  setAnimateDirection: Dispatch<SetStateAction<any>>;
  isDotsAnimated: boolean;
  setIsDotsAnimated: Dispatch<SetStateAction<any>>;
}

const ProfileContext = createContext({});

const ProfileProvider = (props: any): ReactElement => {
  const isBreakpointDown621 = useBreakpointQuery({ key: 'down', value: 621 });

  const [gallerySwiper, setGallerySwiper] = useState(null);
  const [animateDirection, setAnimateDirection] = useState('');
  const [isDotsAnimated, setIsDotsAnimated] = useState(false);
  const [galleryMediaRatioValue, setGalleryMediaRatioValue] = useState('133%');
  const offerBoxContainerRef = useRef(null);
  const photoAspectRatioRef = useRef(null);
  const profileCardBannerRef = useRef(null);
  const profileDetailsRef = useRef<HTMLDivElement>(null);
  const { siteFooterLogoRef, footerTopPosition } = useApp();

  const calculateGalleryMediaRatio = useCallback(() => {
    const photoAspectEl = photoAspectRatioRef?.current;
    const offerActionEl = offerBoxContainerRef?.current;
    const siteFooterEl = siteFooterLogoRef?.current;
    const profileCardBannerEl = profileCardBannerRef?.current;

    if (footerTopPosition > 0 && photoAspectEl && offerActionEl && siteFooterEl) {
      const offerActionClRec = offerActionEl.getBoundingClientRect();

      let cardBannerTop = 0;

      if (profileCardBannerEl) {
        const profileCardBannerClRec = profileCardBannerEl.getBoundingClientRect();

        cardBannerTop = profileCardBannerClRec.top < 0 ? 0 : profileCardBannerClRec.height;
      }

      const OFFER_BOX_MARGIN = 20;

      const dynamicPaddingTop = footerTopPosition - offerActionClRec.height - OFFER_BOX_MARGIN - cardBannerTop;

      setGalleryMediaRatioValue(`${dynamicPaddingTop}px`);
    }
  }, [
    footerTopPosition,
    photoAspectRatioRef?.current,
    offerBoxContainerRef?.current,
    profileCardBannerRef?.current,
    siteFooterLogoRef?.current,
  ]);

  useEffect(() => {
    if (isBreakpointDown621) {
      window.addEventListener('resize', calculateGalleryMediaRatio);
    }

    return () => {
      window.removeEventListener('resize', calculateGalleryMediaRatio);
    };
  }, [isBreakpointDown621, footerTopPosition]);

  const values: IUseProfile = {
    gallerySwiper,
    setGallerySwiper,
    offerBoxContainerRef,
    photoAspectRatioRef,
    profileCardBannerRef,
    calculateGalleryMediaRatio,
    galleryMediaRatioValue,
    profileDetailsRef,
    animateDirection,
    setAnimateDirection,
    isDotsAnimated,
    setIsDotsAnimated,
  };

  return <ProfileContext.Provider value={values} {...props} />;
};

const useProfile = (): IUseProfile => useContext(ProfileContext) as IUseProfile;

export { ProfileProvider, useProfile };
